<template>
  <div class="pub-page">
    <div v-if="loading" class="pub-loading"><i class="mdi mdi-loading mdi-spin"></i> Loading invoice…</div>
    <div v-else-if="!invoice" class="pub-error">
      <i class="mdi mdi-alert-circle"></i>
      <h2>Link not valid</h2>
      <p>This invoice link may have expired or been revoked. Contact your supplier to request a new link.</p>
    </div>
    <template v-else>
      <header class="pub-header">
        <div class="pub-brand">FLOCKR · Tax Invoice</div>
        <div class="pub-supplier">
          <strong>{{ invoice.farm_name || invoice.seller_name }}</strong>
          <span v-if="invoice.farm_phone"> · {{ invoice.farm_phone }}</span>
          <span v-if="invoice.seller_email"> · {{ invoice.seller_email }}</span>
        </div>
      </header>

      <div class="pub-card">
        <div class="pub-top">
          <div>
            <div class="lbl">Billed to</div>
            <div class="big">{{ invoice.company_name }}</div>
            <div v-if="invoice.contact_email" class="muted">{{ invoice.contact_email }}</div>
            <div v-if="invoice.contact_phone" class="muted">{{ invoice.contact_phone }}</div>
            <div v-if="invoice.buyer_kra" class="muted">KRA PIN: {{ invoice.buyer_kra }}</div>
          </div>
          <div class="pub-meta">
            <div><span class="lbl">Invoice</span><strong>{{ invoice.invoice_number }}</strong></div>
            <div><span class="lbl">Issued</span>{{ invoice.issue_date }}</div>
            <div><span class="lbl">Due</span>{{ invoice.due_date }}</div>
            <div v-if="invoice.purchase_order_number"><span class="lbl">PO</span>{{ invoice.purchase_order_number }}</div>
          </div>
        </div>

        <div class="table-responsive"><table class="pub-table">
          <thead><tr><th>#</th><th>Ear Tag</th><th>Slaughter Date</th><th>Grade</th><th class="num">Weight ({{ invoice.billing_weight_basis }})</th><th class="num">Rate/kg</th><th class="num">Amount</th></tr></thead>
          <tbody>
            <tr v-for="(ln, i) in lines" :key="ln.id">
              <td>{{ i+1 }}</td>
              <td>{{ ln.ear_tag || "—" }}</td>
              <td>{{ ln.slaughter_date || "—" }}</td>
              <td>{{ ln.grade || "—" }}</td>
              <td class="num">{{ Number(ln.weight_kg).toFixed(2) }} kg</td>
              <td class="num">{{ fmt(ln.rate_per_kg) }}</td>
              <td class="num"><strong>{{ fmt(ln.amount_kes) }}</strong></td>
            </tr>
          </tbody>
        </table></div>

        <div class="pub-totals">
          <div class="row"><span>Subtotal</span><span>{{ fmt(invoice.subtotal_kes) }}</span></div>
          <div v-if="Number(invoice.vat_kes)>0" class="row"><span>VAT</span><span>{{ fmt(invoice.vat_kes) }}</span></div>
          <div v-if="Number(invoice.wht_kes)>0" class="row"><span>WHT</span><span>−{{ fmt(invoice.wht_kes) }}</span></div>
          <div class="row total"><span>Total {{ invoice.currency }}</span><span>{{ fmt(invoice.total_kes) }}</span></div>
          <div v-if="Number(invoice.paid_kes)>0" class="row paid"><span>Paid</span><span>−{{ fmt(invoice.paid_kes) }}</span></div>
          <div class="row balance"><span>Balance Due</span><span>{{ fmt(invoice.balance_kes) }}</span></div>
        </div>

        <div v-if="canPay" class="pub-pay">
          <h3>Pay Online</h3>
          <p>Use M-Pesa STK push to settle this invoice immediately. Or use M-Pesa Paybill with <strong>{{ invoice.invoice_number }}</strong> as your account reference.</p>
          <div class="pub-pay-row">
            <input v-model="phone" placeholder="M-Pesa phone (e.g. 0712345678)" class="pub-input">
            <input v-model.number="payAmount" type="number" min="1" placeholder="Amount" class="pub-input">
            <button :disabled="paying" class="pub-pay-btn" @click="payNow()"><span v-if="paying"><i class="mdi mdi-loading mdi-spin"></i> Sending…</span><span v-else>Pay via M-Pesa</span></button>
          </div>
          <p v-if="payMsg" class="pub-pay-msg">{{ payMsg }}</p>
        </div>
        <div v-else-if="invoice.status==='paid'" class="pub-paid"><i class="mdi mdi-check-circle"></i> Fully settled — thank you.</div>

        <div v-if="payments.length" class="pub-payments">
          <h3>Payments received</h3>
          <ul>
            <li v-for="p in payments" :key="p.id">{{ p.payment_date }} — KES {{ fmt(p.amount_kes) }} via {{ p.payment_method.replace(/_/g,' ') }}<span v-if="p.payment_ref"> (ref: {{ p.payment_ref }})</span></li>
          </ul>
        </div>

        <footer class="pub-footer">
          <p>Every line in this invoice links back to a specific animal record for traceability.</p>
          <p>Powered by <a href="https://flockr.africa">Flockr</a> — Africa's Livestock Ledger.</p>
        </footer>
      </div>
    </template>
  </div>
</template>

<script>
import { executeJson, flocker } from "@/api";
export default {
  name: "PublicInvoicePage",
  data(){return{
    loading: true,
    invoice: null,
    lines: [],
    payments: [],
    phone: "",
    payAmount: 0,
    paying: false,
    payMsg: "",
  }},
  computed:{
    token(){ return this.$route.params.token; },
    canPay(){ return this.invoice && !["paid","cancelled","draft"].includes(this.invoice.status) && Number(this.invoice.balance_kes) > 0; },
  },
  mounted(){ this.load(); },
  methods:{
    async load(){
      this.loading = true;
      try {
        const r = await executeJson({ function: "getCorporateInvoiceByToken", token: this.token }, flocker);
        if (r.data && r.data.success) {
          this.invoice = r.data.data.invoice;
          this.lines = r.data.data.lines || [];
          this.payments = r.data.data.payments || [];
          this.payAmount = Math.ceil(Number(this.invoice.balance_kes));
        }
      } catch (e){ /* silent */ }
      this.loading = false;
    },
    async payNow(){
      if (!this.phone || this.payAmount <= 0) { alert("Phone and amount required"); return; }
      this.paying = true; this.payMsg = "";
      try {
        const r = await executeJson({ function: "initiateCorporateInvoiceStkPush", token: this.token, phone: this.phone, amount: this.payAmount }, flocker);
        if (r.data && r.data.success) {
          this.payMsg = "M-Pesa request sent to " + this.phone + ". Enter your PIN on your phone to complete.";
        } else this.payMsg = (r.data && r.data.message) || "STK push failed";
      } catch (e){ this.payMsg = e.message; }
      this.paying = false;
    },
    fmt(v){ return Number(v || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}); }
  }
};
</script>

<style scoped>
.pub-page{min-height:100vh;background:#F5EEDE;color:#1A1A1A;padding:30px 16px}
.pub-loading,.pub-error{text-align:center;padding:80px 20px;color:#5C4533;font-size:14px}
.pub-error i{font-size:48px;color:#B71C1C}
.pub-header{max-width:780px;margin:0 auto 16px;text-align:center}
.pub-brand{font-family:Georgia,serif;font-size:13px;color:#8B5A2B;letter-spacing:2px;text-transform:uppercase}
.pub-supplier{font-size:13px;color:#5C4533;margin-top:4px}
.pub-card{max-width:780px;margin:0 auto;background:#FFFFFF;border:1px solid #D8C9A3;border-radius:14px;padding:32px;box-shadow:0 6px 24px rgba(0,0,0,.06)}
.pub-top{display:flex;justify-content:space-between;align-items:flex-start;gap:18px;margin-bottom:20px;padding-bottom:14px;border-bottom:2px solid #F0EBE4}
.pub-top .lbl{font-size:10px;letter-spacing:1px;text-transform:uppercase;color:#8A827A;margin-bottom:3px}
.pub-top .big{font-size:18px;font-weight:700;margin-bottom:3px}
.pub-top .muted{font-size:13px;color:#6B6356}
.pub-meta{text-align:right;display:flex;flex-direction:column;gap:5px;font-size:13px}
.pub-meta strong{font-size:15px;color:#8B3F1E}
.pub-meta .lbl{display:block;font-size:10px;letter-spacing:1px;text-transform:uppercase;color:#8A827A}

.pub-table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:14px}
.pub-table th{background:#F5EEDE;color:#5C4533;padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #D8C9A3}
.pub-table td{padding:8px 10px;border-bottom:1px solid #F0EBE4}
.pub-table th.num,.pub-table td.num{text-align:right}

.pub-totals{margin:14px 0;padding:14px;background:#FBF6E8;border-radius:10px;font-size:13px}
.pub-totals .row{display:flex;justify-content:space-between;padding:3px 0}
.pub-totals .row.total{font-weight:700;font-size:16px;border-top:1px solid #D8C9A3;padding-top:6px;margin-top:6px}
.pub-totals .row.paid{color:#1F6B1F}
.pub-totals .row.balance{font-weight:700;font-size:18px;color:#B71C1C;border-top:1px dashed #D8C9A3;padding-top:6px;margin-top:4px}

.pub-pay{background:#E8F5E9;border:1px solid #C5E1A5;border-radius:10px;padding:18px;margin:20px 0}
.pub-pay h3{margin:0 0 6px;color:#1F6B1F;font-size:16px}
.pub-pay p{margin:0 0 10px;font-size:13px;color:#3A5A3A}
.pub-pay-row{display:flex;gap:8px;flex-wrap:wrap}
.pub-input{flex:1;min-width:160px;padding:9px 11px;border:1px solid #C5E1A5;border-radius:6px;font-size:14px}
.pub-pay-btn{background:#1F6B1F;color:#FFF;border:none;padding:10px 22px;border-radius:6px;font-weight:700;cursor:pointer;font-size:14px}
.pub-pay-btn:disabled{opacity:.6;cursor:not-allowed}
.pub-pay-btn:hover:not(:disabled){background:#15501A}
.pub-pay-msg{margin-top:10px;font-size:13px;color:#3A5A3A;font-weight:600}

.pub-paid{background:#C8E6C9;color:#1F6B1F;padding:14px;border-radius:10px;text-align:center;font-weight:700;margin:20px 0}
.pub-paid i{font-size:20px;vertical-align:middle}

.pub-payments h3{font-size:13px;letter-spacing:1px;text-transform:uppercase;color:#8B5A2B;margin:18px 0 6px}
.pub-payments ul{margin:0;padding-left:18px;font-size:13px;color:#5C4533}
.pub-payments li{margin:3px 0}

.pub-footer{margin-top:24px;padding-top:14px;border-top:1px solid #F0EBE4;font-size:11px;color:#8A827A;text-align:center;line-height:1.5}
.pub-footer p{margin:2px 0}
.pub-footer a{color:#8B5A2B}

@media (max-width:640px){
  .pub-top{flex-direction:column}
  .pub-meta{text-align:left}
  .pub-table{font-size:11px}
  .pub-card{padding:18px}
}
</style>
